import React, { useState, useRef, useEffect } from 'react';
import { Image, Trash2, Send, Loader2, MessageSquare, Clock, CheckCircle } from 'lucide-react';
import { db, auth } from '../../firebase';
import {
  collection, addDoc, serverTimestamp, query,
  where, onSnapshot, limit,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { STREAMS, STREAM_GROUPS, streamsInGroup } from '../../data/doubtStreams';
import MathText from './MathText';
import { toast } from 'react-toastify';
import styles from './StudentDoubtView.module.css';

const R2_ENABLED = process.env.REACT_APP_R2_ENABLED === 'true';
const MAX_CHARS = 1000;

function timeAgo(ts) {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${days}d ago`;
}

function formatDateTime(ts) {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  const d = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
  const t = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${d}, ${t}`;
}

async function compressAndUpload(file) {
  const { default: compress } = await import('browser-image-compression');
  const compressed = await compress(file, { maxSizeMB: 0.3, maxWidthOrHeight: 1200, useWebWorker: true });
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = e => resolve(e.target.result.split(',')[1]);
    reader.readAsDataURL(compressed);
  });
  const token = await auth.currentUser.getIdToken();
  const res = await fetch('/api/upload-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ imageBase64: base64, contentType: 'image/jpeg' }),
  });
  if (!res.ok) throw new Error('Upload service unavailable');
  const { publicUrl } = await res.json();
  return publicUrl;
}

export default function StudentDoubtView({ onOpenDoubt }) {
  const { currentUser, userData } = useAuth();

  // ── Form state ──────────────────────────────────────────────────────
  const [activeGroup, setActiveGroup] = useState(null);
  const [selectedStream, setSelectedStream] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [chapter, setChapter] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // ── My doubts history ───────────────────────────────────────────────
  const [myDoubts, setMyDoubts] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    // No orderBy here — avoids composite index requirement; sort client-side below
    const q = query(
      collection(db, 'doubts'),
      where('userId', '==', currentUser.uid),
      limit(50),
    );
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.submittedAt?.toMillis?.() ?? 0) - (a.submittedAt?.toMillis?.() ?? 0));
      setMyDoubts(docs.slice(0, 30));
      setHistoryLoading(false);
    }, err => { console.error('Doubts history query failed:', err.message); setHistoryLoading(false); });
    return unsub;
  }, [currentUser]);

  // ── Form helpers ────────────────────────────────────────────────────
  const streamsForGroup = activeGroup ? streamsInGroup(activeGroup) : [];
  const subjects = selectedStream ? STREAMS[selectedStream]?.subjects ?? [] : [];
  const comingSoonSubjects = selectedStream ? STREAMS[selectedStream]?.comingSoon ?? [] : [];
  const canSubmit = selectedStream && selectedSubject && (questionText.trim().length >= 10 || imageFile);

  function handleGroupSelect(g) {
    setActiveGroup(g === activeGroup ? null : g);
    setSelectedStream('');
    setSelectedSubject('');
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Select an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10 MB.'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function clearImage() {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      let imageURL = null;
      if (imageFile && R2_ENABLED) {
        try {
          imageURL = await compressAndUpload(imageFile);
        } catch (uploadErr) {
          console.warn('Image upload skipped:', uploadErr.message);
          toast('Image upload unavailable — submitting without it.', { icon: '⚠️' });
        }
      }

      await addDoc(collection(db, 'doubts'), {
        userId: currentUser.uid,
        userName: userData?.username || currentUser.displayName || 'Student',
        userPhotoURL: currentUser.photoURL || '',
        stream: selectedStream,
        subject: selectedSubject,
        chapter: chapter.trim(),
        questionText: questionText.trim(),
        imageURL,
        status: 'open',
        replyCount: 0,
        submittedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      });

      toast.success('Doubt submitted! PrrepX Team will answer soon 🎓');
      setSelectedStream(''); setSelectedSubject(''); setActiveGroup(null);
      setChapter(''); setQuestionText(''); clearImage();
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const pendingCount  = myDoubts.filter(d => d.status !== 'answered').length;
  const answeredCount = myDoubts.filter(d => d.status === 'answered').length;

  return (
    <div className={styles.view}>
      <div className={styles.columns}>

        {/* ── Left col: Ask a Doubt inline form ──────────────────── */}
        <div className={styles.colForm}>
          <div className={styles.formCard}>
          <div className={styles.formHeader}>
            <span className={styles.formToggleIcon}>✏️</span>
            <div>
              <span className={styles.formToggleTitle}>Ask a New Doubt</span>
              <span className={styles.formToggleHint}>Class 6–12 · JEE / NEET · Maths &amp; Science</span>
            </div>
          </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              {/* Step 1: Category group */}
              <div className={styles.field}>
                <label className={styles.label}>1. Select Category</label>
                <div className={styles.chipRow}>
                  {STREAM_GROUPS.map(g => (
                    <button
                      key={g} type="button"
                      className={`${styles.groupChip} ${activeGroup === g ? styles.groupChipActive : ''}`}
                      onClick={() => handleGroupSelect(g)}
                    >{g}</button>
                  ))}
                </div>
              </div>

              {/* Step 2: Stream */}
              {streamsForGroup.length > 0 && (
                <div className={styles.field}>
                  <label className={styles.label}>2. Select Class / Exam</label>
                  <div className={styles.chipRow}>
                    {streamsForGroup.map(k => (
                      <button
                        key={k} type="button"
                        className={`${styles.streamChip} ${selectedStream === k ? styles.streamChipActive : ''}`}
                        onClick={() => { setSelectedStream(k); setSelectedSubject(''); }}
                      >{STREAMS[k].label}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Subject */}
              {(subjects.length > 0 || comingSoonSubjects.length > 0) && (
                <div className={styles.field}>
                  <label className={styles.label}>3. Select Subject</label>
                  <div className={styles.chipRow}>
                    {subjects.map(s => (
                      <button
                        key={s} type="button"
                        className={`${styles.subjectChip} ${selectedSubject === s ? styles.subjectChipActive : ''}`}
                        onClick={() => setSelectedSubject(s)}
                      >{s}</button>
                    ))}
                    {comingSoonSubjects.map(s => (
                      <span key={s} className={styles.subjectChipSoon}>{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Chapter */}
              {selectedSubject && (
                <div className={styles.field}>
                  <label className={styles.label}>4. Chapter <span className={styles.opt}>(optional)</span></label>
                  <input
                    className={styles.input}
                    placeholder="e.g. Thermodynamics, Quadratic Equations…"
                    value={chapter}
                    onChange={e => setChapter(e.target.value)}
                    maxLength={80}
                  />
                </div>
              )}

              {/* Step 5: Question */}
              {selectedSubject && (
                <div className={styles.field}>
                  <label className={styles.label}>
                    5. Your Question
                    <span className={styles.mathTip}>$math$ for equations</span>
                  </label>
                  <textarea
                    className={styles.textarea}
                    placeholder="Describe your doubt clearly. Use $formula$ for inline math."
                    value={questionText}
                    onChange={e => setQuestionText(e.target.value.slice(0, MAX_CHARS))}
                    rows={4}
                  />
                  <span className={`${styles.charCount} ${questionText.length > MAX_CHARS - 50 ? styles.charWarn : ''}`}>
                    {questionText.length}/{MAX_CHARS}
                  </span>
                </div>
              )}

              {/* Step 6: Image (optional) */}
              {selectedSubject && (
                <div className={styles.field}>
                  <label className={styles.label}>6. Attach Image <span className={styles.opt}>(optional)</span></label>
                  {!imagePreview ? (
                    <>
                      <button type="button" className={styles.uploadZone} onClick={() => fileInputRef.current?.click()}>
                        <Image size={20} />
                        <span>Click to attach a photo of your question</span>
                        <span className={styles.uploadHint}>JPEG / PNG / WebP · max 10 MB</span>
                      </button>
                      <input ref={fileInputRef} type="file" accept="image/*" className={styles.hiddenInput} onChange={handleImageChange} />
                    </>
                  ) : (
                    <div className={styles.previewWrap}>
                      <img src={imagePreview} alt="Preview" className={styles.preview} />
                      <button type="button" className={styles.removeBtn} onClick={clearImage}>
                        <Trash2 size={13} /> Remove
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Submit */}
              {selectedSubject && (
                <div className={styles.formFooter}>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={!canSubmit || submitting}
                  >
                    {submitting
                      ? <><Loader2 size={16} className={styles.spin} /> Submitting…</>
                      : <><Send size={16} /> Submit Doubt</>
                    }
                  </button>
                </div>
              )}
            </form>
          </div>{/* /formCard */}
        </div>{/* /colForm */}

        {/* ── Right col: My Doubt History ────────────────────────── */}
        <div className={styles.colHistory}>
        <div className={styles.historyHeader}>
          <h2 className={styles.historyTitle}>📋 My Doubts</h2>
          <div className={styles.historyCounts}>
            <span className={styles.countPill}>
              <Clock size={12} /> {pendingCount} Awaiting
            </span>
            <span className={`${styles.countPill} ${styles.countAnswered}`}>
              <CheckCircle size={12} /> {answeredCount} Answered
            </span>
          </div>
        </div>

        {historyLoading && (
          <div className={styles.historyList}>
            {[1,2,3].map(i => <div key={i} className={styles.skeletonCard} />)}
          </div>
        )}

        {!historyLoading && myDoubts.length === 0 && (
          <div className={styles.emptyHistory}>
            <span className={styles.emptyEmoji}>🙋‍♂️</span>
            <p className={styles.emptyTitle}>No doubts yet</p>
            <p className={styles.emptyHint}>Ask your first doubt above — our team will answer you soon!</p>
          </div>
        )}

        {!historyLoading && myDoubts.length > 0 && (
          <div className={styles.historyList}>
            {myDoubts.map(doubt => (
              <button
                key={doubt.id}
                className={`${styles.historyCard} ${doubt.status === 'answered' ? styles.historyCardAnswered : doubt.lastRepliedByRole === 'student' ? styles.historyCardFollowup : ''}`}
                onClick={() => onOpenDoubt(doubt)}
              >
                <div className={styles.hcTop}>
                  <div className={styles.hcTags}>
                    <span className={styles.streamTag}>{STREAMS[doubt.stream]?.label ?? doubt.stream}</span>
                    {doubt.subject && <span className={styles.subjectTag}>{doubt.subject}</span>}
                    {doubt.chapter && <span className={styles.chapterTag}>{doubt.chapter}</span>}
                  </div>
                  <div className={styles.hcBadgeStack}>
                    <span className={`${styles.statusBadge} ${doubt.status === 'answered' ? styles.statusAnswered : styles.statusOpen}`}>
                      {doubt.status === 'answered' ? '✓ Answered' : '● Awaiting'}
                    </span>
                    {/* only show before student has ever replied — once they reply, badge stays away */}
                    {doubt.status === 'answered' && !doubt.studentHasReplied && (
                      <span className={styles.awaitingFollowup}>↩ Admin Replied</span>
                    )}
                    {doubt.status !== 'answered' && doubt.lastRepliedByRole === 'student' && (
                      <span className={styles.followupSent}>↑ Follow-up Sent</span>
                    )}
                  </div>
                </div>

                <p className={styles.hcQuestion}>
                  {doubt.lastReplyText
                    ? <><span className={styles.hcPreviewBy}>{doubt.lastReplyBy}:</span> <MathText text={doubt.lastReplyText} /></>
                    : <MathText text={doubt.questionText || '(image only)'} />}
                </p>

                <div className={styles.hcBottom}>
                  <div className={styles.hcTimeBlock}>
                    <span className={styles.hcDateTime}>{formatDateTime(doubt.submittedAt)}</span>
                    <span className={styles.hcTimeAgo}>{timeAgo(doubt.submittedAt)}</span>
                    {doubt.lastActivityAt && doubt.replyCount > 0 && (
                      <span className={styles.hcLastActivity}>Last reply: {formatDateTime(doubt.lastActivityAt)}</span>
                    )}
                  </div>
                  {doubt.replyCount > 0 && (
                    <span className={styles.hcReplies}>
                      <MessageSquare size={11} /> {doubt.replyCount} {doubt.replyCount === 1 ? 'answer' : 'answers'}
                    </span>
                  )}
                  <span className={styles.hcView}>View →</span>
                </div>
              </button>
            ))}
          </div>
        )}
        </div>{/* /colHistory */}

      </div>{/* /columns */}
    </div>
  );
}
